import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, EmptyState, Skeleton, StatGrid, StatTile } from "@NP-Webber/crm-kit/dist/next/index.js";
import { useAuth } from "../auth/AuthProvider";
import {
  EMPTY_FORUM_DASHBOARD,
  shellApi,
  type ForumDashboardSummary,
} from "../api/shellApi";
import { getHomePanelsForPack } from "../home/homePanelRegistry";
import { LastUpdate } from "../components/LastUpdate";
import { ScreenHeader } from "../next/page/ScreenHeader";
import { openToday, EVENTS, onEvent } from "../next/events";

type Summary = {
  openTasks: number;
  overdueTasks: number;
  upcomingMeetings: number;
  appBuildVersion: string | null;
  forumDashboard: ForumDashboardSummary;
};

export function TenantHomePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const panels = getHomePanelsForPack(auth.activeTenant?.packKey);
  const isForumDashboard = Boolean(auth.activeTenant?.packKey === "forum-kupot");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    void shellApi
      .getDashboardSummary()
      .then((item) =>
        setSummary({
          openTasks: item.openTasks,
          overdueTasks: item.overdueTasks,
          upcomingMeetings: item.upcomingMeetings,
          appBuildVersion: item.appBuildVersion,
          forumDashboard: item.forumDashboard ?? EMPTY_FORUM_DASHBOARD,
        }),
      )
      .catch(() => {
        setSummary(null);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load, auth.activeTenant?.tenantId]);
  useEffect(() => onEvent(EVENTS.dataChanged, load), [load]);

  return (
    <section className="tenant-home page-section" aria-label={isForumDashboard ? "לוח בקרה" : "תמונת מצב"}>
      <ScreenHeader
        sub={auth.activeTenant?.name ? `${auth.activeTenant.name} · המדדים המרכזיים` : "המדדים המרכזיים של הארגון"}
        more={[
          { key: "refresh", label: "רענון", icon: "refresh", onClick: load },
          { key: "today", label: "היום בתוכנה", icon: "sun", onClick: openToday },
        ]}
      >
        <Button icon="sun" onClick={openToday}>
          היום
        </Button>
      </ScreenHeader>

      {loading && !summary ? (
        <StatGrid>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={84} radius={12} />
          ))}
        </StatGrid>
      ) : summary ? (
        isForumDashboard ? (
          panels.map(({ id, Component }) => <Component key={id} dashboard={summary.forumDashboard} />)
        ) : (
          <>
            <StatGrid min={190}>
              <StatTile label="משימות פתוחות" value={summary.openTasks} icon="task" tone="primary" onClick={() => navigate("/tasks")} />
              <StatTile
                label="משימות באיחור"
                value={summary.overdueTasks}
                icon="alert"
                tone="danger"
                warn={summary.overdueTasks > 0}
                onClick={() => navigate("/tasks")}
              />
              <StatTile label="ישיבות קרובות" value={summary.upcomingMeetings} icon="calendar" tone="violet" onClick={() => navigate("/meetings")} />
              <StatTile label="גרסת מערכת" value={summary.appBuildVersion || "—"} icon="info" tone="neutral" />
            </StatGrid>
            <LastUpdate />
          </>
        )
      ) : (
        <EmptyState
          icon="chart"
          tone={failed ? "danger" : "neutral"}
          title={failed ? "לא הצלחנו לטעון את תמונת המצב" : "אין תוכן להצגה כרגע"}
          action={failed ? <Button onClick={load}>נסו שוב</Button> : null}
        />
      )}
    </section>
  );
}

export default TenantHomePage;
